
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Key, Eye, EyeOff, Map as MapIcon, ShieldCheck, Info, ExternalLink, AlertTriangle, CheckCircle2, Activity, RefreshCw } from 'lucide-react';
import { SystemConfig } from '../../types';
import {
  getRoutingProviderHealthSnapshot,
  type RoutingProviderHealthEvent,
  type RoutingProviderHealthSnapshot,
  type RoutingProviderId,
  type RoutingProviderStatus
} from '../../services/maps/distance';

interface Props {
  config: SystemConfig;
  setConfig: (config: SystemConfig) => void;
}

export const SettingsTabHere: React.FC<Props> = ({ config, setConfig }) => {
  const [showApiKey, setShowApiKey] = useState(false);
  const [healthSnapshot, setHealthSnapshot] = useState<RoutingProviderHealthSnapshot | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);
  
  const routingProvider = config.routingApiProvider ?? 'here';
  const fallbackEnabled = config.routingApiFallbackEnabled ?? true;
  const hereApiKey = config.hereMapsApiKey || '';

  // Validação simples: HERE keys costumam ter 43 caracteres e são alfanuméricas
  const isKeyValid = hereApiKey.length >= 40;
  const isKeyEmpty = hereApiKey.length === 0;

  const loadRoutingHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);

    try {
      const snapshot = await getRoutingProviderHealthSnapshot();
      setHealthSnapshot(snapshot);
    } catch (error: any) {
      setHealthError(error?.message || 'Nao foi possivel carregar o status dos provedores.');
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRoutingHealth();
  }, [loadRoutingHealth]);

  const statusToClass = (status: RoutingProviderStatus): string => {
    if (status === 'healthy') return 'text-status-ready border-status-ready/40 bg-status-ready/10';
    if (status === 'degraded') return 'text-status-pending border-status-pending/40 bg-status-pending/10';
    if (status === 'down') return 'text-status-error border-status-error/40 bg-status-error/10';
    return 'text-text-muted border-border-dim bg-bg-main/40';
  };

  const statusToLabel = (status: RoutingProviderStatus): string => {
    if (status === 'healthy') return 'Saudavel';
    if (status === 'degraded') return 'Degradado';
    if (status === 'down') return 'Indisponivel';
    return 'Sem dados';
  };

  const providerLabel = (provider: RoutingProviderId): string => {
    if (provider === 'here') return 'HERE Maps';
    if (provider === 'google') return 'Google Maps';
    return 'OSRM';
  };

  const formatDateTime = (value: number | null): string => {
    if (!value) return 'Sem registro';
    return new Date(value).toLocaleString('pt-BR');
  };

  const formatLatency = (value: number | null): string => {
    if (!Number.isFinite(value)) return '--';
    return `${Math.round(value as number)} ms`;
  };

  const recentIssues = useMemo(() => {
    if (!healthSnapshot) return [] as RoutingProviderHealthEvent[];

    const merged = Object.values(healthSnapshot.providers)
      .flatMap((providerState) => providerState.history)
      .filter((event) => event.outcome !== 'success')
      .sort((a, b) => b.checkedAt - a.checkedAt);

    return merged.slice(0, 5);
  }, [healthSnapshot]);

  const SectionHeader = ({ icon: Icon, title, subTitle, color = 'text-sal-cyber-teal' }: any) => (
    <div className="flex items-center gap-3 border-b border-border-dim pb-4 mb-6">
      <div className={`p-2.5 bg-card-bg border border-border-dim rounded-xl ${color}`}><Icon className="w-5 h-5" /></div>
      <div>
        <h4 className="text-base font-black text-text-primary uppercase tracking-widest">{title}</h4>
        <p className="text-xs text-text-muted font-bold uppercase tracking-tighter">{subTitle}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-in slide-in-from-left-4 duration-500">
      <section className="bg-card-bg p-6 rounded-3xl border border-border-dim shadow-xl relative overflow-hidden group">
        <SectionHeader icon={Key} title="Configuração HERE Maps" subTitle="Credenciais e preferências de roteamento" color="text-sal-cyber-teal" />
        
        <div className="space-y-6 relative z-10">
          {/* API Key Input */}
          <div className="space-y-2">
            <label htmlFor="hereMapsApiKey" className="text-xs font-black text-text-muted uppercase tracking-widest ml-1 flex items-center gap-2">
              <Key className="w-3.5 h-3.5" /> Chave da API HERE Maps
            </label>
            <div className="relative">
              <input
                id="hereMapsApiKey"
                type={showApiKey ? "text" : "password"}
                value={hereApiKey}
                onChange={(e) => setConfig({ ...config, hereMapsApiKey: e.target.value })}
                placeholder="Insira sua chave da API HERE"
                className={`w-full bg-input-bg border ${isKeyEmpty ? 'border-input-border' : isKeyValid ? 'border-status-ready/50' : 'border-status-error/50'} rounded-2xl pl-5 pr-12 py-4 text-base text-text-primary font-mono focus:border-sal-cyber-teal/50 outline-none transition-all`}
              />
              <button 
                type="button" 
                onClick={() => setShowApiKey(!showApiKey)} 
                className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
              >
                {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {/* Validation Messages */}
            {isKeyEmpty ? (
              <p className="text-[10px] text-status-pending font-bold uppercase tracking-wider ml-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Aviso: A chave está vazia. O roteamento HERE não funcionará.
              </p>
            ) : !isKeyValid ? (
              <p className="text-[10px] text-status-error font-bold uppercase tracking-wider ml-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Erro: O padrão da chave parece inválido (muito curta).
              </p>
            ) : (
              <p className="text-[10px] text-status-ready font-bold uppercase tracking-wider ml-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Chave configurada com sucesso.
              </p>
            )}
            
            <p className="text-[10px] text-text-muted font-bold uppercase tracking-wider ml-1">
              Obtenha sua chave em <a href="https://platform.here.com" target="_blank" rel="noopener noreferrer" className="text-sal-cyber-teal hover:underline inline-flex items-center gap-0.5">platform.here.com <ExternalLink className="w-2.5 h-2.5" /></a>
            </p>
          </div>

          {/* Provider Selector */}
          <div className="space-y-4 pt-4 border-t border-border-dim">
            <p className="text-xs font-black text-text-muted uppercase tracking-widest ml-1 flex items-center gap-2">
              <MapIcon className="w-3.5 h-3.5" /> Provedor de Roteamento Principal
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { id: 'here', label: 'HERE Maps', sub: 'Recomendado', icon: '🎯', color: 'sal-cyber-teal' },
                { id: 'google', label: 'Google Maps', sub: 'Alta Precisão', icon: '💰', color: 'status-info' },
                { id: 'osrm', label: 'OSRM', sub: 'Gratuito', icon: '🆓', color: 'status-ready' }
              ].map((provider) => (
                <button
                  key={provider.id}
                  type="button"
                  onClick={() => setConfig({ ...config, routingApiProvider: provider.id as any })}
                  className={`p-4 rounded-2xl border-2 transition-all text-left flex flex-col gap-1 ${
                    routingProvider === provider.id 
                      ? `border-${provider.color} bg-${provider.color}/10` 
                      : 'border-border-dim hover:border-border-strong'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-black text-sm text-text-primary uppercase tracking-tighter">
                      {provider.icon} {provider.label}
                    </span>
                    <div className={`w-4 h-4 rounded-full border-2 border-${provider.color} flex items-center justify-center`}>
                      {routingProvider === provider.id && <div className={`w-2 h-2 rounded-full bg-${provider.color}`} />}
                    </div>
                  </div>
                  <span className="text-[10px] text-text-muted font-bold uppercase tracking-widest">{provider.sub}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Fallback Toggle */}
          <div className="flex items-center gap-4 p-4 bg-bg-main/50 rounded-2xl border border-border-dim group/toggle">
            <div className="p-2 bg-card-bg rounded-xl border border-border-dim text-sal-cyber-teal">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <label htmlFor="fallbackEnabled" className="text-sm text-text-primary font-black uppercase tracking-tight cursor-pointer block">
                Ativar Fallback Automático
              </label>
              <p className="text-xs text-text-muted font-medium">
                Se o provedor principal falhar, tentar automaticamente o próximo (HERE → Google → OSRM)
              </p>
            </div>
            <div className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                id="fallbackEnabled"
                checked={fallbackEnabled}
                onChange={(e) => setConfig({ ...config, routingApiFallbackEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-border-strong peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sal-cyber-teal"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-card-bg p-6 rounded-3xl border border-border-dim shadow-xl relative overflow-hidden group">
        <SectionHeader icon={Activity} title="Saude do Roteamento" subTitle="Status global dos provedores e fallback" color="text-status-info" />

        <div className="relative z-10 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-text-muted font-bold uppercase tracking-wider">
              Ultima atualizacao: {formatDateTime(healthSnapshot?.updatedAt ?? null)}
            </p>
            <button
              type="button"
              onClick={() => void loadRoutingHealth()}
              disabled={healthLoading}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-border-dim text-[10px] font-black uppercase tracking-widest text-text-primary hover:bg-bg-main/60 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${healthLoading ? 'animate-spin' : ''}`} />
              Atualizar
            </button>
          </div>

          {healthError && (
            <div className="p-3 rounded-xl border border-status-error/30 bg-status-error/10 text-xs text-status-error font-bold">
              {healthError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {(Object.entries(healthSnapshot?.providers ?? {}) as Array<[RoutingProviderId, RoutingProviderHealthSnapshot['providers'][RoutingProviderId]]>).map(([provider, state]) => (
              <article key={provider} className="p-4 rounded-2xl border border-border-dim bg-bg-main/40 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-black text-text-primary uppercase tracking-tight">{providerLabel(provider)}</p>
                  <span className={`px-2 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider ${statusToClass(state.status)}`}>
                    {statusToLabel(state.status)}
                  </span>
                </div>

                <div className="space-y-1 text-xs text-text-secondary font-medium">
                  <p>Ultimo check: {formatDateTime(state.lastCheckAt)}</p>
                  <p>Ultimo sucesso: {formatDateTime(state.lastSuccessAt)}</p>
                  <p>Ultimo erro: {formatDateTime(state.lastErrorAt)}</p>
                  <p>Latencia: {formatLatency(state.lastLatencyMs)}</p>
                </div>

                <div className="grid grid-cols-3 gap-2 text-[10px] font-black uppercase tracking-wider">
                  <div className="p-2 rounded-lg bg-status-ready/10 border border-status-ready/30 text-status-ready text-center">OK {state.successCount}</div>
                  <div className="p-2 rounded-lg bg-status-pending/10 border border-status-pending/30 text-status-pending text-center">Fallback {state.fallbackCount}</div>
                  <div className="p-2 rounded-lg bg-status-error/10 border border-status-error/30 text-status-error text-center">Erro {state.errorCount}</div>
                </div>

                <p className="text-[10px] text-text-muted font-bold uppercase tracking-wider">
                  Erros consecutivos: {state.consecutiveErrors}
                </p>

                {state.lastErrorMessage && (
                  <p className="text-[10px] text-status-error font-bold uppercase tracking-wider line-clamp-3">
                    {state.lastErrorMessage}
                  </p>
                )}
              </article>
            ))}
          </div>

          <div className="p-4 rounded-2xl border border-border-dim bg-bg-main/30 space-y-3">
            <p className="text-xs font-black text-text-primary uppercase tracking-widest">Ocorrencias recentes (fallback / erro)</p>
            {recentIssues.length === 0 ? (
              <p className="text-xs text-text-muted font-medium">Nenhuma ocorrencia recente registrada.</p>
            ) : (
              <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                {recentIssues.map((issue, index) => (
                  <div key={`${issue.provider}-${issue.checkedAt}-${index}`} className="p-2 rounded-xl border border-border-dim bg-card-bg/70 text-xs text-text-secondary">
                    <p className="font-black text-text-primary uppercase tracking-wide">
                      {providerLabel(issue.provider)} • {issue.outcome.toUpperCase()}
                      {issue.fallbackTo ? ` → ${providerLabel(issue.fallbackTo)}` : ''}
                    </p>
                    <p className="font-medium">{new Date(issue.checkedAt).toLocaleString('pt-BR')}</p>
                    {issue.errorMessage && <p className="text-status-error font-medium">{issue.errorMessage}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Free Tier Info Card */}
      <section className="bg-gradient-to-br from-sal-cyber-teal/10 to-transparent p-6 rounded-3xl border border-sal-cyber-teal/20 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10">
          <Info className="w-24 h-24 text-sal-cyber-teal" />
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 bg-sal-cyber-teal/20 rounded-lg text-sal-cyber-teal">
              <Info className="w-4 h-4" />
            </div>
            <h4 className="text-sm font-black text-text-primary uppercase tracking-widest">Limites do Plano Gratuito HERE</h4>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {[
              { id: 'geo', text: '30.000 geocodificações/rotas por mês' },
              { id: 'traffic', text: '5.000 consultas de trânsito por mês' },
              { id: 'matrix', text: '2.500 matrizes de roteamento por mês' },
              { id: 'total', text: 'Total: 250.000 transações/mês' }
            ].map((item) => (
              <div key={item.id} className="flex items-center gap-2 text-xs text-text-secondary font-bold">
                <CheckCircle2 className="w-3.5 h-3.5 text-status-ready" />
                {item.text}
              </div>
            ))}
          </div>
          
          <a 
            href="https://www.here.com/pricing" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-card-bg border border-border-dim rounded-xl text-[10px] font-black text-text-primary uppercase tracking-widest hover:bg-bg-main transition-all"
          >
            Saiba mais sobre preços <ExternalLink className="w-3 h-3 text-sal-cyber-teal" />
          </a>
        </div>
      </section>
    </div>
  );
};
